import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Background from "@/components/landing/Background";
import AppHeader from "@/components/nav/AppHeader";
import { AlertTriangle, CheckCircle2, Loader2, Eye, EyeOff } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Human-readable messages for the `?error=` query params our backend redirects
// users to after a failed OAuth flow (see /auth/google/callback in server.py).
const ERROR_MESSAGES = {
    oauth_failed:           "Помилка автентифікації через Google. Спробуйте ще раз або скористайтесь email + паролем.",
    token_exchange_failed:  "Google не зміг обмінятись токеном. Це тимчасова проблема — спробуйте ще раз через 30 секунд.",
    missing_params:         "Невірне посилання повернення від Google. Натисніть кнопку входу ще раз.",
    link_conflict:          "Цей email уже зареєстрований через інший метод входу. Зайдіть способом, яким реєструвались.",
    google_access_denied:   "Ви відмінили вхід через Google. Якщо це помилково — спробуйте ще раз.",
};

// Friendly mapping of backend error messages → user-friendly UA text.
const passwordErrorText = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    // Lockout — backend returns {code, locked_until} as JSON.
    if (detail.code === "account_locked") {
        const until = new Date(detail.locked_until);
        const minutes = Math.max(1, Math.ceil((until - new Date()) / 60000));
        return `Акаунт тимчасово заблоковано через забагато невдалих спроб. Спробуйте через ${minutes} хв.`;
    }
    return fallback;
};

const Login = () => {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [banner, setBanner] = useState(null);
    const [tab, setTab] = useState("login"); // "login" | "register"

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        // Password-reset success banner (PR #45).
        if (params.get("reset") === "success") {
            setBanner({
                kind:  "success",
                title: "Пароль оновлено",
                text:  "Увійди з новим паролем. З міркувань безпеки ми вилогінили тебе зі всіх інших пристроїв.",
            });
            return;
        }

        // Account-deletion flow banners.
        if (params.get("deletion") === "requested") {
            setBanner({
                kind: "warning",
                title: "Запит на видалення прийнято",
                text:  "Ваш акаунт буде остаточно видалено через 30 днів. До цього часу можете увійти знову і відмінити видалення.",
            });
            return;
        }
        if (params.get("deletion") === "cancelled") {
            setBanner({
                kind: "success",
                title: "Видалення відмінено",
                text:  "Ваш акаунт повністю відновлено. Ласкаво просимо назад!",
            });
            return;
        }

        const err = params.get("error");
        if (err) {
            setBanner({
                kind:  "warning",
                title: "Не вдалося увійти",
                text:  ERROR_MESSAGES[err] || `Помилка: ${err}`,
            });
        }
    }, []);

    // Native Google OAuth (PR #36).
    const handleNativeGoogle = () => {
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        window.location.href = `${backend}/api/auth/google/start?next=/dashboard`;
    };

    // After successful password login/register: route based on user state.
    const routeAfterLogin = async () => {
        // PR: delayed-paywall. Always land on /dashboard after login.
        // The dashboard itself decides whether to show the onboarding
        // hero (incomplete users) or the real widgets (onboarded users).
        // We no longer force-redirect non-payers to /pricing.
        await refresh();
        navigate("/dashboard", { replace: true });
    };

    return (
        <div className="min-h-screen relative" data-testid="login-page">
            <Background />
            <div className="relative z-10 min-h-screen flex flex-col">
                <AppHeader mode="public" />
                <main className="flex-1 flex items-center justify-center px-6 py-8">
                    <div className="w-full max-w-[440px]">
                        {banner && (
                            <div
                                className={`mb-5 rounded-xl p-4 border flex items-start gap-3 ${
                                    banner.kind === "warning"
                                        ? "bg-warning/5 border-warning/40"
                                        : "bg-success/5 border-success/40"
                                }`}
                                data-testid={`login-banner-${banner.kind}`}
                            >
                                {banner.kind === "warning"
                                    ? <AlertTriangle className="text-warning flex-shrink-0 mt-0.5" size={18} />
                                    : <CheckCircle2 className="text-success flex-shrink-0 mt-0.5" size={18} />}
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm text-text-primary">{banner.title}</div>
                                    <div className="text-xs text-secondary-c mt-1 leading-relaxed">{banner.text}</div>
                                </div>
                            </div>
                        )}

                        <div className="glass rounded-2xl p-8">
                            <h1 className="font-display font-extrabold text-3xl text-text-primary text-center" style={{ letterSpacing: "-0.02em" }}>
                                {tab === "login" ? "Увійти в AutoAds" : "Створити акаунт"}
                            </h1>
                            <p className="mt-3 text-center text-sm text-secondary-c">
                                20 хвилин до першої кампанії
                            </p>

                            <button
                                onClick={handleNativeGoogle}
                                data-testid="google-login-btn-native"
                                className="mt-7 w-full flex items-center justify-center gap-3 py-3 rounded-md border border-border bg-surface hover:bg-surface-hover transition text-text-primary font-medium"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                                {tab === "login" ? "Увійти з Google" : "Зареєструватись з Google"}
                            </button>

                            <div className="my-5 flex items-center gap-3">
                                <div className="flex-1 h-px bg-border" />
                                <span className="text-xs text-faint-c uppercase tracking-wider">або email</span>
                                <div className="flex-1 h-px bg-border" />
                            </div>

                            <div className="flex gap-1 mb-5 p-1 bg-surface rounded-lg" role="tablist">
                                <button
                                    role="tab"
                                    aria-selected={tab === "login"}
                                    onClick={() => setTab("login")}
                                    data-testid="tab-login"
                                    className={`flex-1 py-2 rounded-md text-sm font-medium transition ${
                                        tab === "login"
                                            ? "bg-bg-base text-text-primary shadow-sm"
                                            : "text-secondary-c hover:text-text-primary"
                                    }`}
                                >
                                    Увійти
                                </button>
                                <button
                                    role="tab"
                                    aria-selected={tab === "register"}
                                    onClick={() => setTab("register")}
                                    data-testid="tab-register"
                                    className={`flex-1 py-2 rounded-md text-sm font-medium transition ${
                                        tab === "register"
                                            ? "bg-bg-base text-text-primary shadow-sm"
                                            : "text-secondary-c hover:text-text-primary"
                                    }`}
                                >
                                    Реєстрація
                                </button>
                            </div>

                            {tab === "login"
                                ? <LoginForm onSuccess={routeAfterLogin} />
                                : <RegisterForm onSuccess={routeAfterLogin} />}

                        </div>

                        <p className="mt-6 text-center text-sm text-secondary-c">
                            <a href="/" className="hover:text-text-primary transition" data-testid="back-to-landing">
                                ← Назад на головну
                            </a>
                        </p>
                    </div>
                </main>
            </div>
        </div>
    );
};

const LoginForm = ({ onSuccess }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        try {
            await api.post("/auth/login", { email: email.trim(), password });
            await onSuccess();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const status = err?.response?.status;
            if (status === 423) {
                setError(passwordErrorText(detail, "Акаунт тимчасово заблоковано."));
            } else if (status === 401) {
                setError("Невірний email або пароль.");
            } else if (status === 429) {
                setError("Забагато спроб. Зачекайте хвилину і спробуйте знову.");
            } else {
                setError(typeof detail === "string" ? detail : "Не вдалося увійти. Спробуйте ще раз.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-4">
            <div>
                <label htmlFor="login-email" className="block text-xs font-medium text-secondary-c mb-1.5">Email</label>
                <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text-primary placeholder:text-faint-c focus:outline-none focus:border-primary transition"
                    placeholder="you@example.com"
                />
            </div>
            <div>
                <label htmlFor="login-password" className="block text-xs font-medium text-secondary-c mb-1.5">Пароль</label>
                <div className="relative">
                    <input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="login-password-input"
                        className="w-full h-10 rounded-md border border-border bg-surface px-3 pr-10 text-sm text-text-primary placeholder:text-faint-c focus:outline-none focus:border-primary transition"
                        placeholder="••••••••"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-faint-c hover:text-text-primary transition"
                        tabIndex={-1}
                        aria-label={showPassword ? "Сховати пароль" : "Показати пароль"}
                    >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-md bg-error/5 border border-error/30 px-3 py-2 text-xs text-error" data-testid="login-error">
                    {error}
                </div>
            )}

            <button
                type="submit"
                disabled={submitting || !email || !password}
                data-testid="login-submit"
                className="w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2"
            >
                {submitting && <Loader2 className="animate-spin" size={16} />}
                Увійти
            </button>

            <div className="text-center">
                <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-muted-c hover:text-primary transition"
                >
                    Забули пароль?
                </Link>
            </div>
        </form>
    );
};

const RegisterForm = ({ onSuccess }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const hasMinLen = password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password);
    const hasDigit  = /\d/.test(password);

    const canSubmit = !submitting && email && hasMinLen && hasLetter && hasDigit;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError(null);
        setSubmitting(true);
        try {
            await api.post("/auth/register", {
                email: email.trim(),
                password,
                name: name.trim(),
            });
            await onSuccess();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const status = err?.response?.status;
            if (status === 409) {
                setError(typeof detail === "string"
                    ? detail
                    : "Цей email вже зареєстрований. Перейдіть на вкладку «Увійти».");
            } else if (status === 422) {
                setError(typeof detail === "string" ? detail : "Перевірте email та пароль.");
            } else if (status === 429) {
                setError("Забагато спроб реєстрації. Зачекайте годину.");
            } else {
                setError(typeof detail === "string" ? detail : "Не вдалося створити акаунт.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} data-testid="register-form" className="space-y-4">
            <div>
                <label htmlFor="reg-name" className="block text-xs font-medium text-secondary-c mb-1.5">
                    Імʼя <span className="text-faint-c">(необовʼязково)</span>
                </label>
                <input
                    id="reg-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text-primary placeholder:text-faint-c focus:outline-none focus:border-primary transition"
                    placeholder="Як до вас звертатись?"
                />
            </div>
            <div>
                <label htmlFor="reg-email" className="block text-xs font-medium text-secondary-c mb-1.5">Email</label>
                <input
                    id="reg-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="register-email-input"
                    className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text-primary placeholder:text-faint-c focus:outline-none focus:border-primary transition"
                    placeholder="you@example.com"
                />
            </div>
            <div>
                <label htmlFor="reg-password" className="block text-xs font-medium text-secondary-c mb-1.5">Пароль</label>
                <div className="relative">
                    <input
                        id="reg-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="register-password-input"
                        className="w-full h-10 rounded-md border border-border bg-surface px-3 pr-10 text-sm text-text-primary placeholder:text-faint-c focus:outline-none focus:border-primary transition"
                        placeholder="••••••••"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-faint-c hover:text-text-primary transition"
                        tabIndex={-1}
                        aria-label={showPassword ? "Сховати пароль" : "Показати пароль"}
                    >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                    <Hint ok={hasMinLen}>8+ символів</Hint>
                    <Hint ok={hasLetter}>буква</Hint>
                    <Hint ok={hasDigit}>цифра</Hint>
                </div>
            </div>

            {error && (
                <div className="rounded-md bg-error/5 border border-error/30 px-3 py-2 text-xs text-error" data-testid="register-error">
                    {error}
                </div>
            )}

            <button
                type="submit"
                disabled={!canSubmit}
                data-testid="register-submit"
                className="w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2"
            >
                {submitting && <Loader2 className="animate-spin" size={16} />}
                Створити акаунт
            </button>

            <p className="text-[11px] text-faint-c text-center leading-relaxed">
                Створюючи акаунт, ви погоджуєтесь з <a href="/regulamin" className="underline hover:text-secondary-c">Regulamin</a>.
                Ми надішлемо лист для підтвердження email.
            </p>
        </form>
    );
};

const Hint = ({ ok, children }) => (
    <span className={`inline-flex items-center gap-1 ${ok ? "text-success" : "text-faint-c"}`}>
        <span className={`inline-block w-1 h-1 rounded-full ${ok ? "bg-success" : "bg-faint-c"}`} />
        {children}
    </span>
);

export default Login;
